/**
 * SignIn.tsx — Clerk-powered sign-in page for Tinkerlog
 *
 * Uses Clerk's pre-built <SignIn /> component which handles:
 * - Google OAuth
 * - GitHub OAuth
 * - Email + Password
 *
 * Clerk's publishable key must be set in .env:
 *   VITE_CLERK_PUBLISHABLE_KEY=pk_test_...
 *
 * If no key is set (preview mode), renders a placeholder.
 */

import { SignIn } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useThemeStore } from '../../stores/themeStore';
import logoColor from '../../assets/logo-color.png';
import logoMono from '../../assets/logo-mono.png';

const clerkEnabled = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

export default function SignInPage() {
  const { theme, mode } = useThemeStore();
  const useMono = theme === 'blueprint' && mode === 'dark';

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem 1rem',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ display: 'inline-block' }}>
            <img
              src={useMono ? logoMono : logoColor}
              alt="OwensRoot"
              style={{ height: '36px', width: 'auto' }}
            />
          </Link>
          <p
            style={{
              marginTop: '0.5rem',
              fontFamily: 'var(--font-label)',
              fontSize: '0.62rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'var(--color-text-faint)',
            }}
          >
            Access your workbench
          </p>
        </div>

        {clerkEnabled ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <SignIn
              routing="path"
              path="/sign-in"
              signUpUrl="/sign-up"
              afterSignInUrl="/app"
            />
          </div>
        ) : (
          /* Preview mode placeholder */
          <div
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              padding: '2rem',
              textAlign: 'center',
            }}
          >
            <KeyRound
              size={28}
              style={{ color: 'var(--color-text-faint)', marginBottom: '1rem' }}
            />
            <p
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--color-text-muted)',
                marginBottom: '0.75rem',
              }}
            >
              Auth not configured
            </p>
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.8rem',
                color: 'var(--color-text-faint)',
                lineHeight: 1.6,
                marginBottom: '1.5rem',
              }}
            >
              Add{' '}
              <code
                style={{
                  fontFamily: 'var(--font-label)',
                  color: 'var(--color-accent)',
                  fontSize: '0.75rem',
                }}
              >
                VITE_CLERK_PUBLISHABLE_KEY
              </code>{' '}
              to your <code style={{ fontFamily: 'var(--font-label)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>.env</code> file to enable sign-in.
            </p>
            <Link to="/" className="btn-ghost" style={{ display: 'inline-flex' }}>
              ← Back to landing
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
